import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { isOnboardingComplete } from "@/lib/onboardingComplete";
import { GateStrip, gatesFor } from "@/components/admin/GateStrip";

type Row = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  una_name: string | null;
  una_name_status: string | null;
  una_name_confirmed_at: string | null;
  mea_status: string | null;
  mea_signed_at: string | null;
  ca_address: unknown;
  ca_address_option: string | null;
  suite_number: number | null;
  lease_status: string | null;
  lease_signed_at: string | null;
  payment_status: string | null;
  legal_address: unknown;
  created_at: string;
  updated_at: string;
};

function memberName(r: Row) {
  const name = [r.first_name, r.last_name].filter(Boolean).join(" ").trim();
  return name || r.email || "Member";
}

function shortAge(ms: number) {
  const hours = Math.floor(ms / 3600000);
  if (hours < 1) return "just now";
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function AttentionCard({ label, count, to }: { label: string; count: number; to: string }) {
  const quiet = count === 0;
  return (
    <Link
      to={to}
      className={cn(
        "rounded-lg border p-3 transition-colors",
        quiet
          ? "text-muted-foreground hover:bg-muted/40"
          : "border-amber-300/70 bg-amber-500/10 text-amber-900 hover:bg-amber-500/15 dark:text-amber-200",
      )}
    >
      <div className="text-2xl font-semibold tabular-nums">{count}</div>
      <div className="text-xs leading-tight">{label}</div>
    </Link>
  );
}

export default function OnboardingBoard() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [namesWaiting, setNamesWaiting] = useState(0);
  const [syncErrors, setSyncErrors] = useState(0);
  const [suitesExpiring, setSuitesExpiring] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("member_records")
      .select(
        "id, first_name, last_name, email, una_name, una_name_status, una_name_confirmed_at, mea_status, mea_signed_at, ca_address, ca_address_option, suite_number, lease_status, lease_signed_at, payment_status, legal_address, created_at, updated_at",
      )
      .neq("origin", "legacy_import")
      // Dormant people are not in flight, so they never count as stalled.
      .neq("record_status", "dormant");
    setRows((data as Row[]) ?? []);

    const dayAgo = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
    // A suite reservation runs fourteen days. Three days out is the warning line.
    const elevenDaysAgo = new Date(Date.now() - 11 * 24 * 3600 * 1000).toISOString();

    const [waiting, errors, suites] = await Promise.all([
      supabase
        .from("name_review_events")
        .select("id", { count: "exact", head: true })
        .is("decision", null)
        .lt("submitted_at", dayAgo),
      supabase
        .from("sync_errors")
        .select("id", { count: "exact", head: true })
        .gte("created_at", monthAgo),
      supabase
        .from("suite_numbers")
        .select("number, member_record_id, reserved_at, status")
        .eq("status", "reserved")
        .lt("reserved_at", elevenDaysAgo),
    ]);

    setNamesWaiting(waiting.count ?? 0);
    setSyncErrors(errors.count ?? 0);

    const reservedIds = (suites.data ?? [])
      .map((s: { member_record_id: string | null }) => s.member_record_id)
      .filter(Boolean) as string[];
    const unsigned = (data as Row[] | null)?.filter(
      (r) => reservedIds.includes(r.id) && r.lease_status !== "signed" && !r.lease_signed_at,
    );
    setSuitesExpiring(unsigned?.length ?? 0);

    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const items = useMemo(() => {
    const now = Date.now();
    return rows
      .filter((r) => !isOnboardingComplete(r as never))
      .map((r) => {
        const gates = gatesFor(r);
        const current = gates.find((g) => g.state !== "passed") ?? null;
        const lastPassedAt = gates
          .filter((g) => g.state === "passed" && g.at)
          .map((g) => new Date(g.at as string).getTime())
          .sort((a, b) => b - a)[0];
        const since = lastPassedAt ?? new Date(r.updated_at || r.created_at).getTime();
        const ms = Math.max(0, now - since);
        return { row: r, gates, current, ms };
      })
      .sort((a, b) => b.ms - a.ms);
  }, [rows]);

  const stalled = useMemo(
    () => items.filter((i) => i.ms >= 48 * 3600 * 1000).length,
    [items],
  );

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Onboarding</h1>
          <p className="text-sm text-muted-foreground">
            Every client still giving us what we need, and where they stand.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{items.length} in onboarding</span>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AttentionCard label="Names waiting over 24 hours" count={namesWaiting} to="/admin/names" />
        <AttentionCard label="Stalled over 48 hours" count={stalled} to="/admin/onboarding" />
        <AttentionCard label="Sync errors" count={syncErrors} to="/admin/onboarding" />
        <AttentionCard
          label="Suites expiring within 3 days"
          count={suitesExpiring}
          to="/admin/suites"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Where everyone stands</CardTitle>
          <CardDescription>
            Sorted with the longest wait at the top. Once payment lands, they move to the fulfillment board.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Loading</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nobody is mid onboarding right now.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Gates</TableHead>
                  <TableHead>Current gate</TableHead>
                  <TableHead className="text-right">Time at gate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(({ row, gates, current, ms }) => {
                  const hours = ms / 3600000;
                  const tone =
                    hours >= 120
                      ? "bg-destructive/10 hover:bg-destructive/15"
                      : hours >= 48
                        ? "bg-amber-500/10 hover:bg-amber-500/15"
                        : undefined;
                  return (
                    <TableRow key={row.id} className={cn(tone)}>
                      <TableCell>
                        <Link to={`/admin/members/${row.id}`} className="block">
                          <span className="font-medium">{memberName(row)}</span>
                          <span className="block text-xs text-muted-foreground">{row.email}</span>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <GateStrip record={row} gates={gates} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {current ? current.waiting : "All done, moving to fulfillment"}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{shortAge(ms)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
